import { type JSX, useEffect, useMemo, useState } from 'react';
import { useMediaQuery } from 'react-responsive';
import { cx } from '@linaria/core';
import { Button as ReakitButton, type ButtonProps as ReakitButtonProps } from 'reakit';

import { isTabletOrDesktopQuery } from '@cx-modules/react-utils';
import { type BookieCode, type BookmakerConfig } from '@cx-modules/types';

import OCLogo from './OCLogo';
import {
  bookieLogo,
  button,
  buttonUnsupported,
  buttonWithBadge,
  buttonWithVerticalBadge,
  drifter,
  drifterFlash,
  logoWrapper,
  noAnimation,
  ocLogoWrapper,
  selectedBet,
  steamer,
  steamerFlash,
  textWrapper,
} from './style';

export type OddsButtonProps = ReakitButtonProps & {
  bookieCode?: BookieCode | 'OC';
  bookmakerConfig?: Partial<Pick<BookmakerConfig, 'brandColour' | 'logoLarge' | 'logoSmall'>>;
  disableAnimation?: boolean;
  disabled?: boolean;
  isSelected?: boolean;
  isUnsupported?: boolean;
  isVerticalBadgeLogo?: boolean;
  newUI?: boolean;
  renderBadge?: boolean;
  steamOrDrift?: string;
  tooltip?: JSX.Element | null;
};

export interface UseFlashOptions {
  classNamesFactory(isFlashing: boolean): string[];
  initialClassNames?: string[];
}

export const useFlash = ({ classNamesFactory }: UseFlashOptions): [string[], () => void] => {
  const [classNames, setClassNames] = useState<string[]>([]);

  useEffect(() => {
    setClassNames(classNamesFactory(true));
  }, [classNamesFactory]);

  const handleTransitionEnd = () => {
    setClassNames(classNamesFactory(false));
  };

  return [classNames, handleTransitionEnd];
};

export const OddsButton = ({
  bookieCode,
  bookmakerConfig,
  children,
  className,
  disableAnimation,
  disabled,
  isSelected,
  isUnsupported,
  isVerticalBadgeLogo = false,
  renderBadge = false,
  steamOrDrift,
  tooltip,
  ...props
}: OddsButtonProps): JSX.Element => {
  const classNamesFactory = useMemo(
    () => getButtonClassNames(steamOrDrift, disableAnimation),
    [steamOrDrift, disableAnimation],
  );

  const [classNames, handleTransitionEnd] = useFlash({
    classNamesFactory,
  });

  const isTabletOrDesktop = useMediaQuery(isTabletOrDesktopQuery);

  const isOCNonClickable = bookieCode === 'OC_NON_CLICKABLE';
  const isOc = bookieCode === 'OC';

  const logoLarge = bookmakerConfig?.logoLarge;
  const logoSmall = bookmakerConfig?.logoSmall;

  const showVerticalBadge = !isTabletOrDesktop && renderBadge && isVerticalBadgeLogo;
  const logoToRender = !isTabletOrDesktop && isVerticalBadgeLogo ? logoLarge : logoSmall;

  const renderLogo = () => {
    if (isOCNonClickable) {
      return (
        <div className={ocLogoWrapper}>
          <OCLogo />
        </div>
      );
    }

    if (renderBadge && logoToRender) {
      return (
        <div
          className={logoWrapper}
          style={!isOc && bookmakerConfig?.brandColour ? { backgroundColor: bookmakerConfig.brandColour } : undefined}
        >
          <img
            alt={'bookie logo'}
            className={bookieLogo}
            src={logoToRender}
            style={!isOc && bookmakerConfig?.brandColour ? { backgroundColor: bookmakerConfig.brandColour } : undefined}
          />
        </div>
      );
    }

    return null;
  };

  return (
    <ReakitButton
      className={cx(
        button,
        ...(isSelected && !disabled && !isUnsupported ? [selectedBet] : classNames),
        isUnsupported ? buttonUnsupported : '',
        renderBadge || isOCNonClickable ? buttonWithBadge : '',
        showVerticalBadge ? buttonWithVerticalBadge : '',
        className ? className : '',
      )}
      data-testid="odds-button"
      onTransitionEnd={handleTransitionEnd}
      {...props}
    >
      {renderLogo()}
      <div className={textWrapper}>{children}</div>
      {tooltip}
    </ReakitButton>
  );
};

function getButtonClassNames(steamOrDrift: string | undefined, disableAnimation?: boolean) {
  return (isFlashing: boolean) => {
    const baseClasses = [];

    // Add color classes based on movement
    switch (steamOrDrift) {
      case 'drifting':
        baseClasses.push(drifter);
        if (isFlashing && !disableAnimation) {
          baseClasses.push(drifterFlash);
        }
        break;
      case 'shortening':
        baseClasses.push(steamer);
        if (isFlashing && !disableAnimation) {
          baseClasses.push(steamerFlash);
        }
        break;
      default:
        break;
    }

    // Add noAnimation class if animations are disabled
    if (disableAnimation) {
      baseClasses.push(noAnimation);
    }

    return baseClasses;
  };
}
